import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { DEPARTMENTS, NAVICA } from "@/lib/constants";
import usePageMeta from "@/hooks/usePageMeta";

const DEPT_ACCENT = {
  coral: { bg: "#FCE7EB", ink: "#E2718A", ring: "rgba(226,113,138,0.25)" },
  orange: { bg: "#FBE5D0", ink: "#EA823A", ring: "rgba(234,130,58,0.25)" },
  navy: { bg: "#DCE4E6", ink: "#284952", ring: "rgba(40,73,82,0.25)" },
};

export default function Services() {
  usePageMeta({
    title: "Services & Departments, Navica Supports",
    description:
      "Our three departments: NDIS (Support Coordination, PSRC, Early Childhood, Employment), Social Work (Advocacy, Private, DVA, OOHC, Prison/Custody, Juvenile Justice), and Mind & Motion (athlete mental health).",
  });

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16 relative overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#EA823A] font-semibold mb-5 fade-up">Our Departments</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#1F2A2E] leading-[1.05] mb-6 fade-up fade-up-1">
            Three departments. <span className="italic" style={{ color: "var(--navica-coral)" }}>One team.</span>
          </h1>
          <p className="text-xl text-[#4A595E] leading-relaxed max-w-3xl fade-up fade-up-2">
            Everything we do sits inside one of three departments. Each is rooted in social work, shaped by the people we walk beside, and built to meet you where you are.
          </p>

          {/* Anchor jump links */}
          <div className="mt-10 flex flex-wrap gap-2 fade-up fade-up-3" data-testid="dept-jump-nav">
            {DEPARTMENTS.map((d, i) => {
              const c = DEPT_ACCENT[d.color] || DEPT_ACCENT.coral;
              return (
                <a
                  key={d.slug}
                  href={`#${d.slug}`}
                  className="text-xs uppercase tracking-[0.15em] font-semibold px-4 py-2.5 rounded-full border transition-all hover:-translate-y-0.5"
                  style={{ borderColor: c.ring, color: c.ink, background: "white" }}
                  data-testid={`dept-jump-${d.slug}`}
                >
                  0{i + 1} · {d.name}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* DEPARTMENTS + SERVICES */}
      <section className="pb-20" data-testid="departments-detail">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 space-y-24 lg:space-y-32">
          {DEPARTMENTS.map((dept, idx) => {
            const c = DEPT_ACCENT[dept.color] || DEPT_ACCENT.coral;
            return (
              <article
                key={dept.slug}
                id={dept.slug}
                className="scroll-mt-28"
                data-testid={`dept-section-${dept.slug}`}
              >
                {/* Department masthead */}
                <div className="mb-10 grid lg:grid-cols-12 gap-10 items-end">
                  <div className="lg:col-span-8">
                    <div className="flex items-center gap-3 mb-5">
                      <span
                        className="font-display text-3xl tabular-nums"
                        style={{ color: c.ink }}
                      >
                        0{idx + 1}
                      </span>
                      <span className="w-12 h-px" style={{ background: c.ink, opacity: 0.5 }} />
                      <span className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: c.ink }}>
                        Department
                      </span>
                    </div>
                    <h2 className="font-display text-5xl md:text-6xl text-[#1F2A2E] leading-[1.05] mb-4">
                      {dept.name}
                    </h2>
                    <p className="text-lg md:text-xl text-[#4A595E] leading-relaxed max-w-2xl">
                      {dept.tagline}
                    </p>
                  </div>
                  <div className="lg:col-span-4 lg:text-right">
                    <span
                      className="inline-block text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full"
                      style={{ background: c.bg, color: c.ink }}
                    >
                      {dept.services.length} {dept.services.length === 1 ? "service" : "services"}
                    </span>
                  </div>
                </div>

                {/* Services in this department */}
                <div className="space-y-4">
                  {dept.services.map((s) => (
                    <div
                      key={s.title}
                      className="bg-white border border-[#284952]/10 rounded-2xl p-6 lg:p-8 transition-all duration-300 hover:border-[color:var(--navica-coral)]"
                      data-testid={`dept-${dept.slug}-service-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 mt-1"
                          style={{ background: c.bg, color: c.ink }}
                        >
                          <CheckCircle2 className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <h3 className="font-display text-2xl md:text-3xl text-[#1F2A2E] leading-tight">
                              {s.title}
                            </h3>
                            {s.upcoming && (
                              <span
                                className="text-[9px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full bg-[#F1E4D5] text-[#6B7A80]"
                                data-testid={`upcoming-${dept.slug}`}
                              >
                                Upcoming
                              </span>
                            )}
                          </div>
                          <p className="text-[#4A595E] leading-relaxed mb-4">{s.short}</p>

                          {s.points && s.points.length > 0 && (
                            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-4 mt-2 border-t border-[#284952]/10">
                              {s.points.map((pt) => (
                                <li key={pt} className="flex gap-2.5 text-sm text-[#4A595E] leading-relaxed">
                                  <span className="mt-2 shrink-0 w-1 h-1 rounded-full" style={{ background: c.ink }} />
                                  <span>{pt}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-28 bg-[#1A363D] text-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">
            Not sure where <span className="italic" style={{ color: "var(--navica-coral-soft)" }}>you fit</span>?
          </h2>
          <p className="text-lg text-white/80 leading-relaxed mb-10 max-w-2xl mx-auto">
            You don&apos;t need to know which department applies to you before reaching out. Tell us what&apos;s going on and we&apos;ll figure it out together, no obligation, no jargon.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/referrals" className="btn-primary" data-testid="services-cta-refer">
              Make a Referral <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${NAVICA.phoneRaw}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border border-white/40 text-white hover:bg-white/10 transition-all"
              data-testid="services-cta-call"
            >
              Call {NAVICA.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
